import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Cloud,
  CloudOff,
  Key,
  Save,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  ExternalLink,
  ListChecks,
  Folder,
  FolderTree,
  FolderPlus,
  FileText,
  Pause,
  Play,
  PauseCircle,
} from 'lucide-react';

const STATUS_BADGES = {
  ok: { color: 'bg-green-100 text-green-700 border-green-200', labelKey: 'adminCloud.status.ok' },
  ko: { color: 'bg-red-100 text-red-700 border-red-200', labelKey: 'adminCloud.status.ko' },
  skipped: { color: 'bg-slate-100 text-slate-700 border-slate-200', labelKey: 'adminCloud.status.skipped' },
};

const docTypeLabel = (type, t) => {
  const map = {
    ticket: 'adminCloud.docType.ticket',
    facture: 'adminCloud.docType.facture',
    acompte: 'adminCloud.docType.acompte',
    devis: 'adminCloud.docType.devis',
    bon_commande: 'adminCloud.docType.bonCommande',
  };
  return map[type] ? t(map[type]) : type;
};

const AdminCloudSettingsPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, formatDate } = useLanguage();

  // Phase 2.3 - cloud sync toggle
  const [syncStatus, setSyncStatus] = useState({ enabled: true, paused_at: null, paused_by: null });
  const [togglingSync, setTogglingSync] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  // Form
  const [apiUrl, setApiUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  // Logs / stats
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilterStatus, setLogFilterStatus] = useState('');
  const [logFilterType, setLogFilterType] = useState('');

  // G2 — folder hierarchy preview
  const [preview, setPreview] = useState(null);
  const [rebuildingHier, setRebuildingHier] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, st, lg, pv] = await Promise.all([
        api.get('/admin/cloud-settings'),
        api.get('/admin/cloud-uploads/stats'),
        api.get('/admin/cloud-uploads/log', {
          params: { limit: 100, status: logFilterStatus || undefined, doc_type: logFilterType || undefined },
        }),
        api.get('/admin/cloud-folders/preview').catch(() => ({ data: null })),
      ]);
      setSettings(s.data);
      setApiUrl(s.data.api_url || 'https://remobiles.fr/api/v1/public/upload');
      setEnabled(!!s.data.enabled);
      setStats(st.data);
      setLogs(lg.data.items || []);
      setPreview(pv.data);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || t('adminCloud.errors.load') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get('/admin/cloud-settings/sync-status').then(r => setSyncStatus(r?.data || { enabled: true })).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => { if (!loading) fetchAll(); /* eslint-disable-next-line */ }, [logFilterStatus, logFilterType]);

  const handleSave = async () => {
    setSaving(true); setMessage(null);
    try {
      const payload = { api_url: apiUrl, enabled };
      if (newKey.trim()) payload.api_key = newKey.trim();
      await api.put('/admin/cloud-settings', payload);
      setMessage({ type: 'success', text: t('adminCloud.save.success') });
      setNewKey('');
      await fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || t('adminCloud.errors.save') });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true); setMessage(null);
    try {
      const r = await api.post('/admin/cloud-settings/test');
      if (r.data.ok) {
        setMessage({
          type: 'success',
          text: t('adminCloud.test.ok', {
            name: r.data.name || 'cloud',
            owner: r.data.owner || '?',
            expires: r.data.expires_at || '?',
          }),
        });
      } else {
        setMessage({ type: 'error', text: t('adminCloud.test.ko', { error: r.data.error || t('adminCloud.test.unknownError') }) });
      }
      await fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || t('adminCloud.errors.test') });
    } finally {
      setTesting(false);
    }
  };

  const handleRebuildHierarchy = async () => {
    setRebuildingHier(true); setMessage(null);
    try {
      const r = await api.post('/admin/cloud-folders/rebuild-current-month');
      if (r.data.ok) {
        setMessage({
          type: 'success',
          text: t('adminCloud.hierarchy.ok', {
            path: r.data.expected_path,
            folderId: r.data.folder_id?.slice(0, 8),
          }),
        });
      } else {
        setMessage({ type: 'error', text: t('adminCloud.hierarchy.ko', { message: r.data.message }) });
      }
      await fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || t('adminCloud.errors.rebuild') });
    } finally {
      setRebuildingHier(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12 text-slate-500">{t('adminCloud.access.adminRequired')}</div>
      </Layout>
    );
  }

  const byStatus = stats?.by_status || {};
  const totalUploads = (byStatus.ok || 0) + (byStatus.ko || 0) + (byStatus.skipped || 0);

  const handleSyncToggleConfirm = async (newEnabled) => {
    setConfirmToggle(null);
    setTogglingSync(true);
    setMessage(null);
    try {
      const r = await api.post('/admin/cloud-settings/sync-toggle', { enabled: newEnabled });
      setSyncStatus(r?.data?.status || { enabled: newEnabled });
      setMessage({ type: 'success', text: newEnabled ? t('adminCloud.sync.resumed') : t('adminCloud.sync.paused') });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || t('adminCloud.errors.syncToggle') });
    } finally {
      setTogglingSync(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-cloud-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
              <Cloud className="w-7 h-7 text-blue-600" />
              {t('adminCloud.title')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {t('adminCloud.subtitlePre')}{' '}
              <a href="https://remobiles.fr/docs" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                remobiles.fr <ExternalLink className="w-3 h-3" />
              </a>.
            </p>
          </div>
          <button
            onClick={fetchAll}
            className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2 text-sm"
            data-testid="cloud-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" /> {t('adminCloud.refresh')}
          </button>
        </div>

        {/* Message bandeau */}
        {message && (
          <div
            className={`px-4 py-3 rounded-lg border flex items-start gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
            data-testid="cloud-message-banner"
          >
            {message.type === 'success' ? <CheckCircle className="w-5 h-5 mt-0.5" /> : <AlertCircle className="w-5 h-5 mt-0.5" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Phase 2.3 - Sync toggle */}
        <div className={`rounded-xl border p-4 ${syncStatus.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`} data-testid="cloud-sync-toggle-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {syncStatus.enabled ? (
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : (
                <PauseCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`font-semibold ${syncStatus.enabled ? 'text-emerald-800' : 'text-amber-800'}`} data-testid="cloud-sync-status-label">
                  {syncStatus.enabled ? t('adminCloud.sync.active') : t('adminCloud.sync.pausedLabel')}
                </p>
                {!syncStatus.enabled && syncStatus.paused_at && (
                  <p className="text-xs text-amber-700 mt-1">
                    {t('adminCloud.sync.pausedAt', { date: formatDate(syncStatus.paused_at) })}
                    {syncStatus.paused_by ? t('adminCloud.sync.pausedBy', { by: syncStatus.paused_by }) : ''}
                  </p>
                )}
                {syncStatus.enabled && (
                  <p className="text-xs text-emerald-700 mt-1">
                    {t('adminCloud.sync.activeHint')}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setConfirmToggle({ newEnabled: !syncStatus.enabled })}
              disabled={togglingSync}
              data-testid="cloud-sync-toggle-btn"
              className={`px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2 disabled:opacity-50 transition-colors ${syncStatus.enabled ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
            >
              {togglingSync ? <Loader2 className="w-4 h-4 animate-spin" /> : (syncStatus.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />)}
              {syncStatus.enabled ? t('adminCloud.sync.pauseBtn') : t('adminCloud.sync.resumeBtn')}
            </button>
          </div>
        </div>

        {confirmToggle && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmToggle(null)} data-testid="cloud-sync-confirm-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {confirmToggle.newEnabled ? t('adminCloud.confirm.resumeTitle') : t('adminCloud.confirm.pauseTitle')}
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                {confirmToggle.newEnabled
                  ? t('adminCloud.confirm.resumeBody')
                  : t('adminCloud.confirm.pauseBody')}
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmToggle(null)} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm" data-testid="cloud-sync-confirm-cancel">{t('adminCloud.cancel')}</button>
                <button onClick={() => handleSyncToggleConfirm(confirmToggle.newEnabled)} className={`px-4 py-2 rounded-lg text-white font-medium text-sm ${confirmToggle.newEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'}`} data-testid="cloud-sync-confirm-ok">
                  {confirmToggle.newEnabled ? t('adminCloud.sync.resumeBtn') : t('adminCloud.sync.pauseBtn')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label={t('adminCloud.stats.globalStatus')}
            value={enabled ? t('adminCloud.stats.enabled') : t('adminCloud.stats.disabled')}
            icon={enabled ? <Cloud className="w-5 h-5 text-green-600" /> : <CloudOff className="w-5 h-5 text-slate-400" />}
            tone={enabled ? 'green' : 'slate'}
          />
          <StatCard label={t('adminCloud.stats.uploadsOk')} value={byStatus.ok || 0} icon={<CheckCircle className="w-5 h-5 text-green-600" />} tone="green" />
          <StatCard label={t('adminCloud.stats.uploadsKo')} value={byStatus.ko || 0} icon={<XCircle className="w-5 h-5 text-red-600" />} tone={byStatus.ko ? 'red' : 'slate'} />
          <StatCard label={t('adminCloud.stats.total')} value={totalUploads} icon={<ListChecks className="w-5 h-5 text-blue-600" />} tone="blue" />
        </div>

        {/* G2 — Structure preview */}
        <div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
          data-testid="structure-preview-section"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="font-semibold flex items-center gap-2 text-slate-700">
              <FolderTree className="w-5 h-5" /> {t('adminCloud.structure.title')}
            </h2>
            <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Folder className="w-4 h-4 text-amber-500" />
                {t('adminCloud.structure.cachedTotal', { count: preview?.folders_cached_total ?? 0 })}
              </span>
              <span className="inline-flex items-center gap-1">
                <FolderPlus className="w-4 h-4 text-green-600" />
                {t('adminCloud.structure.createdThisMonth', { count: preview?.folders_cached_this_month ?? 0 })}
              </span>
              <button
                onClick={handleRebuildHierarchy}
                disabled={rebuildingHier}
                className="px-3 py-1.5 text-xs bg-amber-50 border border-amber-200 hover:bg-amber-100 text-amber-700 rounded inline-flex items-center gap-1 disabled:opacity-60"
                data-testid="cloud-rebuild-hierarchy-btn"
              >
                {rebuildingHier ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {t('adminCloud.structure.retryHierarchy')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tree mock — current month */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-700" data-testid="structure-tree-preview">
              {(() => {
                const now = new Date();
                // Noms de dossiers distants réels (remobiles.fr les crée en français) — ne pas traduire.
                const moisFR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
                const joursFR = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
                const monthLabel = `${moisFR[now.getMonth()]}-${now.getFullYear()}`;
                const dayLabel = `${joursFR[now.getDay()]}-${String(now.getDate()).padStart(2,'0')}`;
                const recent = preview?.recent_uploads || [];
                return (
                  <>
                    <div>📁 remobiles/</div>
                    <div className="pl-4">📁 {monthLabel}/</div>
                    <div className="pl-8">📁 {dayLabel}/</div>
                    {recent.length === 0 ? (
                      <div className="pl-12 text-slate-400 italic">{t('adminCloud.structure.noUploadToday')}</div>
                    ) : (
                      recent.slice(0, 8).map((u) => (
                        <div key={`${u.doc_type}-${u.doc_ref}`} className="pl-12 truncate">
                          📄 {u.filename || `${u.doc_type}_${u.doc_ref}.pdf`}
                          {u.hierarchy_ok === false && <span className="ml-2 text-amber-600 not-italic">{t('adminCloud.structure.flat')}</span>}
                        </div>
                      ))
                    )}
                  </>
                );
              })()}
            </div>

            {/* Recent folders cache */}
            <div className="rounded-lg border border-slate-200 p-4 text-sm" data-testid="structure-folders-cache">
              <div className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Folder className="w-4 h-4 text-amber-500" /> {t('adminCloud.structure.recentFolders')}
              </div>
              {!preview?.recent_folders?.length ? (
                <div className="text-xs text-slate-400 italic">{t('adminCloud.structure.noFolderCache')}</div>
              ) : (
                <ul className="space-y-1">
                  {preview.recent_folders.map((fd) => (
                    <li key={fd.key} className="text-xs flex items-start gap-2">
                      <Folder className="w-3 h-3 mt-0.5 text-amber-400 flex-shrink-0" />
                      <span className="font-mono truncate">{fd.remote_path || fd.name}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-slate-400 mt-3">
                ⚠️ {t('adminCloud.structure.policyLabel')} <strong>NO-DELETE</strong>{t('adminCloud.structure.policyBody')}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            {t('adminCloud.loading')}
          </div>
        ) : (
          <>
            {/* Settings form */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold flex items-center gap-2 text-slate-700"><Key className="w-5 h-5" /> {t('adminCloud.connection.title')}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t('adminCloud.connection.uploadUrl')}>
                  <input
                    type="text"
                    value={apiUrl}
                    onChange={(e) => setApiUrl(e.target.value)}
                    placeholder="https://remobiles.fr/api/v1/public/upload"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                    data-testid="cloud-api-url-input"
                  />
                </Field>
                <Field label={t('adminCloud.connection.status')}>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      className="w-4 h-4"
                      data-testid="cloud-enabled-toggle"
                    />
                    <span className="text-sm">{t('adminCloud.connection.enableAuto')}</span>
                  </label>
                </Field>
              </div>

              <Field label={t('adminCloud.connection.currentKey', { masked: settings?.api_key_masked || t('adminCloud.connection.noKey') })}>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showKey ? 'text' : 'password'}
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      placeholder={t('adminCloud.connection.keyPlaceholder')}
                      className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 font-mono text-sm"
                      autoComplete="off"
                      data-testid="cloud-api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="cloud-toggle-show-key"
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">{t('adminCloud.connection.keyHint')}</p>
              </Field>

              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2 text-sm"
                  data-testid="cloud-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {t('adminCloud.save.btn')}
                </button>
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-2 text-sm"
                  data-testid="cloud-test-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {t('adminCloud.test.btn')}
                </button>

                {settings?.last_test_status && (
                  <span className="text-xs text-slate-500 ml-auto">
                    {t('adminCloud.test.lastTest', {
                      mark: settings.last_test_status === 'ok' ? '✓' : '✗',
                      date: settings.last_test_at ? formatDate(settings.last_test_at) : '',
                    })}
                    {settings.last_test_error && <span className="text-red-600 ml-2">({settings.last_test_error})</span>}
                  </span>
                )}
              </div>
            </div>

            {/* Logs */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="font-semibold flex items-center gap-2 text-slate-700"><ListChecks className="w-5 h-5" /> {t('adminCloud.logs.title')}</h2>
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={logFilterStatus}
                    onChange={(e) => setLogFilterStatus(e.target.value)}
                    className="px-2 py-1 text-xs border border-slate-200 rounded"
                    data-testid="cloud-filter-status"
                  >
                    <option value="">{t('adminCloud.logs.allStatuses')}</option>
                    <option value="ok">{t('adminCloud.logs.filterOk')}</option>
                    <option value="ko">{t('adminCloud.logs.filterKo')}</option>
                    <option value="skipped">{t('adminCloud.logs.filterSkipped')}</option>
                  </select>
                  <select
                    value={logFilterType}
                    onChange={(e) => setLogFilterType(e.target.value)}
                    className="px-2 py-1 text-xs border border-slate-200 rounded"
                    data-testid="cloud-filter-type"
                  >
                    <option value="">{t('adminCloud.logs.allTypes')}</option>
                    <option value="ticket">{t('adminCloud.logs.typeTickets')}</option>
                    <option value="facture">{t('adminCloud.logs.typeFactures')}</option>
                    <option value="acompte">{t('adminCloud.logs.typeAcomptes')}</option>
                    <option value="devis">{t('adminCloud.logs.typeDevis')}</option>
                    <option value="bon_commande">{t('adminCloud.logs.typeBonsCommande')}</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
                      <th className="py-2 px-2">{t('adminCloud.logs.colDate')}</th>
                      <th className="py-2 px-2">{t('adminCloud.logs.colType')}</th>
                      <th className="py-2 px-2">{t('adminCloud.logs.colRef')}</th>
                      <th className="py-2 px-2">{t('adminCloud.logs.colStatus')}</th>
                      <th className="py-2 px-2">{t('adminCloud.logs.colFolder')}</th>
                      <th className="py-2 px-2">{t('adminCloud.logs.colError')}</th>
                    </tr>
                  </thead>
                  <tbody data-testid="cloud-logs-tbody">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-400">{t('adminCloud.logs.empty')}</td>
                      </tr>
                    ) : (
                      logs.map((l) => {
                        const badge = STATUS_BADGES[l.status] || STATUS_BADGES.skipped;
                        return (
                          <tr key={`${l.doc_type}-${l.doc_ref}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-2 text-xs whitespace-nowrap text-slate-600">
                              {l.attempted_at ? formatDate(l.attempted_at) : '-'}
                            </td>
                            <td className="py-2 px-2 text-xs">{docTypeLabel(l.doc_type, t)}</td>
                            <td className="py-2 px-2 font-mono text-xs">{l.doc_ref}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs border ${badge.color}`}>{t(badge.labelKey)}</span>
                            </td>
                            <td className="py-2 px-2 text-xs text-slate-500 truncate max-w-xs">{l.folder_path || '-'}</td>
                            <td className="py-2 px-2 text-xs text-red-600">{l.error_msg || ''}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

const StatCard = ({ label, value, icon, tone = 'slate' }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-slate-500">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-800">{value}</div>
    </div>
  );
};

export default AdminCloudSettingsPage;
